import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { consumerApi } from 'lib/consumer-api';
import { API_ENDPOINTS } from 'constants/endpoints';
import { Swords, Eye } from 'lucide-react';
import ConsumerListPageLayout from 'components/consumer/ConsumerListPageLayout';
import { ConsumerFilterField, consumerFilterInputClass, consumerFilterInputMonoClass } from 'components/consumer/ConsumerFilterBar';
import {
  CONSUMER_TD_CLASS,
  CONSUMER_TH_CLASS,
  CONSUMER_TH_ACTION_CLASS,
  CONSUMER_EYE_BTN_CLASS,
  CONSUMER_CELL_HEADING,
  CONSUMER_CELL_MUTED,
  CONSUMER_CELL_TEXT,
} from 'components/consumer/consumer-list-styles';
import ConsumerUuidCell from 'components/consumer/ConsumerUuidCell';
import { appendCursorParams, parseCursorPagination } from 'lib/cursor-pagination';
import { useCursorListPage } from 'hooks/useCursorListPage';
import { hasPlayerFilter, playerFiltersFromUrl } from 'lib/player-url-filters';
import { BATTLE_STATUS_LABEL, labelFor, statusPillByLabel } from 'constants/enums';
import type { ConsumerPortalBattle } from 'types/consumer';

type BattleFilters = {
  uuid: string;
  winner: string;
  status: '' | 'running' | 'active' | 'completed' | 'abandoned';
  userId: string;
  username: string;
};

const emptyFilters: BattleFilters = { uuid: '', winner: '', status: '', userId: '', username: '' };

const countFilters = (f: BattleFilters) =>
  (f.uuid.trim() ? 1 : 0) +
  (f.winner.trim() ? 1 : 0) +
  (f.status ? 1 : 0) +
  (hasPlayerFilter(f) ? 1 : 0);

function buildFilterParams(filters: BattleFilters) {
  const params = new URLSearchParams();
  if (filters.userId.trim()) {
    params.set('userId', filters.userId.trim());
  } else if (filters.username.trim()) {
    params.set('username', filters.username.trim());
  }
  if (filters.uuid.trim()) params.set('uuid', filters.uuid.trim());
  if (filters.winner.trim()) params.set('winner', filters.winner.trim());
  if (filters.status) params.set('status', filters.status);
  return params;
}

const ConsumerBattles = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [draftFilters, setDraftFilters] = useState<BattleFilters>(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState<BattleFilters>(emptyFilters);
  const [playerFilterLabel, setPlayerFilterLabel] = useState('');

  const fetchPage = useCallback(async (cursor: number | null) => {
    const params = appendCursorParams(buildFilterParams(appliedFilters), cursor);
    const res = await consumerApi.get<ConsumerPortalBattle[]>(`${API_ENDPOINTS.CONSUMER_PORTAL.BATTLES}?${params.toString()}`);
    const meta = parseCursorPagination(res);
    return { items: res.success && Array.isArray(res.data) ? res.data : [], nextCursor: meta.nextCursor, hasMore: meta.hasMore, total: meta.total, totalPages: meta.totalPages };
  }, [appliedFilters]);

  const { page, items, loading, refreshing, total, totalPages, limit, resetAndLoad, refreshCurrent, goFirst, goToPage, goLast } = useCursorListPage(fetchPage);

  useEffect(() => {
    if (location.pathname !== '/consumer/battles') return;
    const playerFilters = playerFiltersFromUrl(location.search);
    if (playerFilters) {
      const next = { ...emptyFilters, ...playerFilters };
      setDraftFilters(next);
      setAppliedFilters(next);
      setPlayerFilterLabel(playerFilters.username);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  useEffect(() => {
    resetAndLoad();
  }, [appliedFilters, resetAndLoad]);

  const clearAllFilters = () => {
    setDraftFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPlayerFilterLabel('');
    navigate('/consumer/battles', { replace: true });
  };

  const clearPlayerFilter = () => {
    const next = { ...appliedFilters, userId: '', username: '' };
    setDraftFilters((prev) => ({ ...prev, userId: '', username: '' }));
    setAppliedFilters(next);
    setPlayerFilterLabel('');
    navigate('/consumer/battles', { replace: true });
  };

  return (
    <ConsumerListPageLayout
      icon={Swords} title="Battles" description="Recent battles for your consumer (read-only)."
      totalCount={null} filteredCount={items.length}
      loading={loading} refreshing={refreshing} onRefresh={() => refreshCurrent()}
      activeFilterCount={countFilters(appliedFilters)} draftFilterCount={countFilters(draftFilters)}
      onApplyFilters={() => setAppliedFilters({ ...draftFilters })}
      onClearFilters={clearAllFilters}
      playerFilterLabel={hasPlayerFilter(appliedFilters) ? playerFilterLabel || appliedFilters.username || `User #${appliedFilters.userId}` : undefined}
      onClearPlayerFilter={hasPlayerFilter(appliedFilters) ? clearPlayerFilter : undefined}
      filterFields={
        <>
          <ConsumerFilterField label="Battle ID">
            <input
              type="text"
              placeholder="Partial ID"
              value={draftFilters.uuid}
              onChange={(e) => setDraftFilters((p) => ({ ...p, uuid: e.target.value }))}
              className={consumerFilterInputMonoClass}
            />
          </ConsumerFilterField>
          <ConsumerFilterField label="Winner"><input type="text" placeholder="Search winner" value={draftFilters.winner} onChange={(e) => setDraftFilters((p) => ({ ...p, winner: e.target.value }))} className={consumerFilterInputClass} /></ConsumerFilterField>
          <ConsumerFilterField label="Status">
            <select value={draftFilters.status} onChange={(e) => setDraftFilters((p) => ({ ...p, status: e.target.value as BattleFilters['status'] }))} className={consumerFilterInputClass}>
              <option value="">All statuses</option><option value="running">Running</option><option value="active">Active</option><option value="completed">Completed</option><option value="abandoned">Abandoned</option>
            </select>
          </ConsumerFilterField>
        </>
      }
      tableTitle="Battle History" tableSubtitle="Latest battles scoped to your consumer"
      emptyIcon={Swords} emptyTitle="No battles yet" emptyDescription="Battles will appear here once players start games."
      isDatasetEmpty={total === 0 && !loading && countFilters(appliedFilters) === 0}
      isFilteredEmpty={items.length === 0 && !loading && countFilters(appliedFilters) > 0}
      colSpan={8} page={page} itemCount={items.length} limit={limit} total={total} totalPages={totalPages} onFirst={goFirst} onPageChange={goToPage} onLast={goLast}
      tableHead={
        <tr>
          <th className={CONSUMER_TH_CLASS}>Battle #</th>
          <th className={CONSUMER_TH_CLASS}>Battle ID</th>
          <th className={CONSUMER_TH_CLASS}>Status</th>
          <th className={CONSUMER_TH_CLASS}>Variant</th>
          <th className={CONSUMER_TH_CLASS}>Pot</th>
          <th className={CONSUMER_TH_CLASS}>Winner</th>
          <th className={CONSUMER_TH_CLASS}>Created</th>
          <th className={CONSUMER_TH_ACTION_CLASS}>Action</th>
        </tr>
      }
      tableBody={
        <>
          {items.map((b) => {
            const statusLabel = labelFor(BATTLE_STATUS_LABEL, b.status);
            return (
              <tr key={b.id} className="hover:bg-subtle/40 transition-colors">
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <span className={`${CONSUMER_CELL_HEADING} font-mono`}>BTL-{b.id}</span>
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <ConsumerUuidCell value={b.uuid} fallback={null} label="battle ID" />
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <span className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase border ${statusPillByLabel(statusLabel)}`}>
                    {statusLabel}
                  </span>
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <span className={CONSUMER_CELL_TEXT}>{b.variantName || b.variantCode || '—'}</span>
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <span className={CONSUMER_CELL_HEADING}>₹{Number(b.potAmount).toLocaleString()}</span>
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <span className={CONSUMER_CELL_TEXT}>{b.winnerUsername || '—'}</span>
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <span className={CONSUMER_CELL_MUTED}>
                    {b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}
                  </span>
                </td>
                <td className={`${CONSUMER_TD_CLASS} whitespace-nowrap`}>
                  <button
                    type="button"
                    onClick={() => navigate(`/consumer/battles/${b.id}`)}
                    title="View battle"
                    aria-label="View battle"
                    className={CONSUMER_EYE_BTN_CLASS}
                  >
                    <Eye size={16} strokeWidth={2} />
                  </button>
                </td>
              </tr>
            );
          })}
        </>
      }
    />
  );
};

export default ConsumerBattles;
